import * as XLSX from 'xlsx';
import { parseExcelData, convertGoogleSheetsUrl, getUniqueSettings, getUniqueExperiments } from './dataProcessing';

export const handleExcelFileUpload = async (file, { setIsLoading, setError, setMethodData, setExperimentsMetadata, setFileInfo, setSelectedMethod, setSelectedModel, setComparisonModel, setSelectedMethodsForComparison, setAvailableSettings, setAvailableExperiments }) => {
  if (!file) return;

  setIsLoading(true);
  setError('');

  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const { data: parsedData, metadata } = parseExcelData(workbook);

    if (Object.keys(parsedData).length === 0) {
      throw new Error('No valid data found in Excel file. Please ensure the format matches expectations (Sheet name as method, first row as models, first column as language-docCount labels).');
    }

    setMethodData(parsedData);
    setExperimentsMetadata(metadata);

    const firstMethodKey = Object.keys(parsedData)[0];
    const firstModelKey = Object.keys(parsedData[firstMethodKey] || {})[0];

    // Extract available settings and experiments
    const settings = getUniqueSettings(metadata);
    const experiments = getUniqueExperiments(metadata);
    setAvailableSettings(settings);
    setAvailableExperiments(experiments);

    setFileInfo({
      name: file.name,
      methods: Object.keys(parsedData),
      models: Object.keys(parsedData[firstMethodKey] || {}),
      settings,
      experiments
    });

    setSelectedMethod(firstMethodKey);
    setSelectedModel(firstModelKey);
    setComparisonModel(firstModelKey);
    // Start with no methods selected for comparison - let user choose
    setSelectedMethodsForComparison([]);

  } catch (err) {
    setError(`Error parsing Excel file: ${err.message}`);
    console.error('Excel parsing error:', err);
  } finally {
    setIsLoading(false);
  }
};

export const handleLoadFromGoogleSheets = async (googleSheetsUrl, { setIsLoading, setError, setMethodData, setExperimentsMetadata, setFileInfo, setSelectedMethod, setSelectedModel, setComparisonModel, setSelectedMethodsForComparison, setAvailableSettings, setAvailableExperiments }) => {
  if (!googleSheetsUrl.trim()) {
    setError('Please enter a Google Sheets URL');
    return;
  }

  const exportUrl = convertGoogleSheetsUrl(googleSheetsUrl);
  if (!exportUrl) {
    setError('Invalid Google Sheets URL format. Please ensure it is a valid Google Sheets "d/sheetId" URL.');
    return;
  }

  setIsLoading(true);
  setError('');

  try {
    const response = await fetch(exportUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}. Check URL or sharing settings.`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const { data: parsedData, metadata } = parseExcelData(workbook);

    if (Object.keys(parsedData).length === 0) {
      throw new Error('No valid data found in Google Sheets. Please ensure the sheet has data and the format matches expectations.');
    }

    setMethodData(parsedData);
    setExperimentsMetadata(metadata);

    const firstMethodKey = Object.keys(parsedData)[0];
    const firstModelKey = Object.keys(parsedData[firstMethodKey] || {})[0];

    // Extract available settings and experiments
    const settings = getUniqueSettings(metadata);
    const experiments = getUniqueExperiments(metadata);
    setAvailableSettings(settings);
    setAvailableExperiments(experiments);

    setFileInfo({
      name: 'Google Sheets Data',
      methods: Object.keys(parsedData),
      models: Object.keys(parsedData[firstMethodKey] || {}),
      settings,
      experiments
    });

    setSelectedMethod(firstMethodKey);
    setSelectedModel(firstModelKey);
    setComparisonModel(firstModelKey);

    // Pre-select "LLM parametric, query-wise" experiments for all settings by default
    const allMethods = Object.keys(parsedData);
    const defaultSelectedMethods = allMethods.filter(method => {
      const lowerMethod = method.toLowerCase();
      return lowerMethod.includes('llm parametric') && lowerMethod.includes('query-wise');
    });
    setSelectedMethodsForComparison(defaultSelectedMethods);

  } catch (err) {
    setError(`Error loading from Google Sheets: ${err.message}`);
    console.error('Google Sheets loading error:', err);
  } finally {
    setIsLoading(false);
  }
};